/** Detail arithmetic: bounded chart series from the server's values (U031, U032); nothing is recomputed. */
import { CHART_POINTS_MAX } from "../../contracts/constants.ts";
import type { Bar, DetailRow, Levels } from "../../contracts/views.ts";

export interface Point {
  readonly start: string;
  readonly end: string;
  readonly openPaise: bigint;
  readonly highPaise: bigint;
  readonly lowPaise: bigint;
  readonly closePaise: bigint;
  readonly volume: number;
  readonly developing: boolean;
  readonly gap: boolean;
  readonly stale: boolean;
  readonly vwapPaise: bigint | null;
  /** How many source bars this point stands for (1 unless aggregated). */
  readonly merged: number;
}

export interface Series {
  readonly points: readonly Point[];
  readonly aggregated: boolean;
  readonly minPaise: bigint;
  readonly maxPaise: bigint;
}

function pointOf(bar: Bar, vwap: bigint | null): Point {
  return {
    start: bar.start,
    end: bar.end,
    openPaise: bar.openPaise,
    highPaise: bar.highPaise,
    lowPaise: bar.lowPaise,
    closePaise: bar.closePaise,
    volume: bar.volume,
    developing: bar.state === "DEVELOPING",
    gap: bar.gap,
    stale: bar.stale,
    vwapPaise: vwap,
    merged: 1,
  };
}

function combine(a: Point, b: Point): Point {
  return {
    start: a.start,
    end: b.end,
    openPaise: a.openPaise,
    highPaise: b.highPaise > a.highPaise ? b.highPaise : a.highPaise,
    lowPaise: b.lowPaise < a.lowPaise ? b.lowPaise : a.lowPaise,
    closePaise: b.closePaise,
    volume: a.volume + b.volume,
    developing: b.developing,
    gap: a.gap || b.gap,
    stale: a.stale || b.stale,
    vwapPaise: b.vwapPaise,
    merged: a.merged + b.merged,
  };
}

/** Keep the newest half of the budget as raw bars and merge older bars into equal groups. */
export function aggregate(
  points: readonly Point[],
  max: number,
): { points: Point[]; aggregated: boolean } {
  if (points.length <= max) {
    return { points: [...points], aggregated: false };
  }
  const recent = Math.floor(max / 2);
  const older = points.slice(0, points.length - recent);
  const slots = max - recent;
  const size = Math.ceil(older.length / slots);
  const merged: Point[] = [];
  for (let at = 0; at < older.length; at += size) {
    merged.push(older.slice(at, at + size).reduce(combine));
  }
  return { points: [...merged, ...points.slice(points.length - recent)], aggregated: true };
}

/** Build the plotted series (at most `max` points) and its price extent, levels included. */
export function buildSeries(detail: DetailRow, max = CHART_POINTS_MAX): Series {
  const raw = detail.bars.map((bar, index) => pointOf(bar, detail.vwapPaise[index] ?? null));
  const { points, aggregated } = aggregate(raw, max);
  const prices: bigint[] = [
    ...points.flatMap((p) => [p.highPaise, p.lowPaise]),
    ...levelValues(detail.levels),
  ];
  const first = prices[0] ?? 0n;
  return {
    points,
    aggregated,
    minPaise: prices.reduce((low, v) => (v < low ? v : low), first),
    maxPaise: prices.reduce((high, v) => (v > high ? v : high), first),
  };
}

/** The server's level values that are present. */
export function levelValues(levels: Levels): bigint[] {
  return [
    levels.openingRangeHighPaise,
    levels.openingRangeLowPaise,
    levels.entryPaise,
    levels.stopPaise,
    levels.exitPaise,
  ].filter((value): value is bigint => value !== null);
}

/** The data-quality notes the chart must state in text: gaps, stale inputs, a developing bar, uncertain fills. */
export function chartNotes(detail: DetailRow, series: Series): string[] {
  const notes: string[] = [];
  const developing = series.points.filter((p) => p.developing).length;
  if (developing > 0) {
    notes.push(`${String(developing)} developing bar(s): not yet complete.`);
  }
  const gaps = series.points.filter((p) => p.gap).length;
  if (gaps > 0) {
    notes.push(`${String(gaps)} bar(s) follow a data gap.`);
  }
  const stale = series.points.filter((p) => p.stale).length;
  if (stale > 0) {
    notes.push(`${String(stale)} bar(s) rest on stale inputs.`);
  }
  if (detail.uncertainExecution) {
    notes.push("Intrabar execution is uncertain: a chart touch does not establish a fill.");
  }
  if (series.aggregated) {
    notes.push("Older intervals are aggregated to stay within the plotted-point bound.");
  }
  return notes;
}
